import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, IconButton, Tooltip } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/Toast";
import { useRegenerateMemberCodeMutation } from "../../api/userApi";
import { useGetMeQuery } from "../../api/authApi";
import { codeDigits, formatCode } from "../management/components/memberCode";
import RegenerateMemberCodeConfirm from "./modals/RegenerateMemberCodeConfirm";
import { resetTour } from "../../lib/tour";
import DeleteAccountConfirm from "./modals/DeleteAccountConfirm";
import { AccountHeader, FixedFooter, RowGroup, SectionLabel, SoftRow } from "./components/AccountSoft";
import { dangerButtonSx } from "./actionStyles";
import "./darkSurface.css";

const ROLE_KEYS = {
  User: "role_user",
  Admin: "role_admin",
};

function formatJoined(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
}

/**
 * Hesap Ayarları (mobildeki gibi menü): üstte ad ve e-posta şeridi, altında
 * yumuşak satırlar. Profil bilgileri ve şifre ayrı sayfalarda; hesabı silme
 * en altta dolu kırmızı düğme, onay penceresiyle iki adımlı.
 */
export default function AccountPage() {
  const { t } = useScriptMode();
  const { user, setUser } = useAuth();
  // Kod tek kullanımlık: biri kodla eklenince sunucu yeniler. Sayfa her
  // açılışta oturumu tazeler (AppShell yeni kodu kullanıcıya yazar).
  useGetMeQuery(undefined, { refetchOnMountOrArgChange: true });
  const navigate = useNavigate();
  const toast = useToast();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenerate, { isLoading: regenerating }] = useRegenerateMemberCodeMutation();
  const memberCode = codeDigits(user?.memberCode);

  // Kişisel kod: yönetici kişiyi e-posta + bu kodla ekler. Panoya gruplamasız gider.
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(memberCode);
      toast.success(t("Kod kopyalandı"));
    } catch {
      toast.error(t("Kod kopyalanamadı"));
    }
  };

  const handleRegenerate = async () => {
    const result = await regenerate();
    if (result.error) {
      toast.error(result.error.message || t("Kod yenilenemedi"));
      return;
    }
    const next = result.data?.data?.memberCode;
    if (next) setUser({ ...(user || {}), memberCode: next });
    setRegenOpen(false);
    toast.success(t("Kodunuz yenilendi"));
  };

  const userId = user?._id || user?.id || null;
  const initial = (user?.name || user?.email || "?").slice(0, 1).toUpperCase();
  const fullName = `${user?.name || ""} ${user?.surname || ""}`.trim();
  const joined = formatJoined(user?.createdAt);
  const goals = user?.goalDetail || [];
  const groupCount = (user?.groupIds || []).length;

  const replayTour = () => {
    resetTour(userId);
    navigate("/uygulama/profil");
  };

  return (
    <div className="tk-dark w-full max-w-3xl mx-auto flex flex-col gap-2">
      <AccountHeader title={t("Hesap Ayarları")} />

      {/* Üstte sabit kimlik şeridi */}
      <div className="sticky top-0 z-10 mt-2 flex items-center gap-4 px-4 py-3 rounded-2xl bg-terakkiBrandCloud border border-terakkiBorder">
        <div className="w-12 h-12 rounded-full bg-terakkiPrimaryDark text-white flex items-center justify-center text-xl font-SemiBold shrink-0">
          {initial}
        </div>
        <div className="min-w-0">
          <div className="text-base font-SemiBold text-terakkiInk truncate">{fullName || t("profile_title")}</div>
          <div className="text-xs text-terakkiNeutral truncate">{user?.email}</div>
        </div>
      </div>

      <SectionLabel>{t("Profil")}</SectionLabel>
      <RowGroup>
        <SoftRow Icon={PersonOutlineIcon} title={t("Profil Bilgileri")} meta={fullName || "—"} to="/uygulama/hesap/profil" />
        <SoftRow Icon={LockOutlinedIcon} title={t("Şifre Değiştir")} meta="••••••••" to="/uygulama/hesap/sifre" />
      </RowGroup>

      <SectionLabel>{t("Kişisel Kod")}</SectionLabel>
      <RowGroup>
        {memberCode ? (
          <div className="w-full flex items-center gap-3 px-4 py-3 border-b border-terakkiBorder">
            <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-terakkiBrandCloud text-terakkiPrimaryDark">
              <VpnKeyOutlinedIcon fontSize="small" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-base text-terakkiInk tracking-wider tabular-nums" data-member-code>
                {formatCode(memberCode)}
              </span>
              <span className="block text-xs text-terakkiNeutral truncate">
                {t("Kodum · yönetici seni e-posta ve bu kodla gruba ekler")}
              </span>
            </span>
            <Tooltip title={t("Kopyala")}>
              <IconButton onClick={copyCode} aria-label={t("Kodu kopyala")} size="small">
                <ContentCopyIcon fontSize="small" className="text-terakkiPrimaryDark dark:text-white/80" />
              </IconButton>
            </Tooltip>
          </div>
        ) : null}
        <p data-member-code-note className="px-4 py-2.5 text-xs leading-4 text-terakkiNeutral border-b border-terakkiBorder dark:text-white/60">
          {t("member_code_one_time")}
        </p>
        <SoftRow
          Icon={RefreshIcon}
          title={t("Yeni kod al")}
          meta={t("Eski kod hemen geçersiz olur")}
          onClick={() => setRegenOpen(true)}
        />
      </RowGroup>

      <SectionLabel>{t("Hesap Bilgileri")}</SectionLabel>
      <RowGroup>
        <SoftRow Icon={MailOutlineIcon} title={t("profile_field_email")} meta={user?.email || "—"} />
        <SoftRow Icon={ShieldOutlinedIcon} title={t("profile_field_role")} meta={t(ROLE_KEYS[user?.role] || "role_user")} />
        {joined ? <SoftRow Icon={EventAvailableIcon} title={t("profile_field_joined")} meta={joined} /> : null}
        <SoftRow
          Icon={FlagOutlinedIcon}
          title={t("profile_my_goals")}
          meta={goals.length ? goals.map((g) => `${g.label} (${g.goalValue})`).join(" · ") : t("results_no_goals")}
        />
        <SoftRow Icon={GroupsIcon} title={t("profile_my_groups")} meta={`${t("profile_group_count")}: ${groupCount}`} />
      </RowGroup>

      <SectionLabel>{t("Yardım")}</SectionLabel>
      <RowGroup>
        <SoftRow Icon={HelpOutlineIcon} title={t("Tanıtımı Tekrar İzle")} onClick={replayTour} />
      </RowGroup>

      <SectionLabel>{t("profile_danger_zone")}</SectionLabel>
      <p className="text-xs text-terakkiNeutral px-1">{t("profile_delete_account_desc")}</p>

      {/* Hesabı Sil altta sabit, dolu kırmızı; onay penceresi ikinci adım */}
      <FixedFooter>
        <Button
          fullWidth
          size="large"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => setDeleteOpen(true)}
          sx={{ ...dangerButtonSx, borderRadius: 999 }}
        >
          {t("profile_delete_account")}
        </Button>
      </FixedFooter>

      <DeleteAccountConfirm open={deleteOpen} onClose={() => setDeleteOpen(false)} />
      <RegenerateMemberCodeConfirm
        open={regenOpen}
        loading={regenerating}
        onClose={() => setRegenOpen(false)}
        onConfirm={handleRegenerate}
      />
    </div>
  );
}
